import React, { Fragment, useState } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { XMarkIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { Button } from 'components/ui';
import { getCurrencyIcon } from 'utils/paymentFormatters.jsx';
import { GatewaySelection } from './GatewaySelection';
import { PaymentDetailsForm } from './PaymentDetailsForm';
import { PaymentLinkSuccess } from './PaymentLinkSuccess';
import { AcademicFeeForm } from './AcademicFeeForm';
import { HostelFeeMatrix } from './HostelFeeMatrix';

export const PaymentLinkModal = ({
  isOpen,
  paymentLinkStep,
  gateways,
  isLoadingGateways,
  selectedGateway,
  paymentCatalogs,
  isLoadingCatalogs,
  selectedCatalog,
  applicationForms,
  isLoadingApplicationForms,
  selectedApplicationForm,
  paymentFormData,
  isCreatingLink,
  createdLinkData,
  copySuccess,
  handleClose,
  handleSelectGateway,
  handleSelectCatalog,
  handleInputChange,
  handleBulkUpdate,
  handleCreatePaymentLink,
  handleCopyPaymentLink,
  setPaymentLinkStep,
  setSelectedApplicationForm,
  isMobile,
  isFeePayment,
  isInternational,
  leadData,
  feeBreakup,
  setFeeBreakup,
}) => {
  // Dynamic steps based on gateway type
  let PAYMENT_LINK_STEPS = selectedGateway?.GatewayCode === 'MAN' 
    ? [
        { key: 'gateway', title: 'Gateway', description: 'Choose provider' },
        { key: 'details', title: 'Details', description: 'Enter payment info' },
        { key: 'success', title: 'Complete', description: 'Payment recorded' },
      ]
    : [
        { key: 'gateway', title: 'Gateway', description: 'Choose provider' },
        { key: 'details', title: 'Details', description: 'Enter payment info' },
        { key: 'success', title: 'Share', description: 'Copy and share link' },
      ];

  if (isFeePayment) {
    PAYMENT_LINK_STEPS.splice(1, 0, 
      { key: 'academic_fee', title: 'Academic', description: 'Course Selection' },
      { key: 'hostel_fee', title: 'Hostel', description: 'Accomodation Matrix' }
    );
  }

  const activeStepIndex = Math.max(
    PAYMENT_LINK_STEPS.findIndex((step) => step.key === paymentLinkStep),
    0
  );

  const getModalTitle = () => {
    switch (paymentLinkStep) {
      case 'gateway':
        return 'Select Payment Gateway';
      case 'academic_fee':
        return 'Academic Fee Configuration';
      case 'hostel_fee':
        return 'Hostel Fee Configuration';
      case 'details':
        return 'Payment Details';
      case 'success':
        return selectedGateway?.GatewayCode === 'MAN' ? 'Manual Payment Recorded' : 'Payment Link Created';
      default:
        return selectedGateway?.GatewayCode === 'MAN' ? 'Record Manual Payment' : 'Create Payment Link';
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-y-auto px-4 py-6 sm:px-5"
        onClose={()=>{}}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="dark:bg-dark-700 relative flex w-full max-w-xl origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 outline-none focus:outline-none">
            {/* Header */}
            <div className="dark:bg-dark-800 flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 sm:px-5">
              <DialogTitle
                as="h3"
                className="dark:text-dark-100 flex items-center gap-2 text-base font-medium text-gray-800"
              >
                {getCurrencyIcon(paymentFormData.currency, 'h-5 w-5 text-green-600')}
                {getModalTitle()}
              </DialogTitle>
              <Button
                onClick={handleClose || (() => {})}
                variant="flat"
                isIcon
                className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
                disabled={isCreatingLink}
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">

              {paymentLinkStep === 'gateway' && (
                <GatewaySelection
                  gateways={gateways}
                  isLoading={isLoadingGateways}
                  onSelectGateway={handleSelectGateway}
                />
              )}

              {paymentLinkStep === 'academic_fee' && (
                <AcademicFeeForm
                  isInternational={isInternational}
                  feeBreakup={feeBreakup}
                  setFeeBreakup={setFeeBreakup}
                  onNext={(academicData) => {
                    handleInputChange({ 
                      target: { 
                        name: 'amount', 
                        value: academicData.amount.toString() 
                      } 
                    });
                    // Explicitly set currency based on international status
                    handleInputChange({ 
                      target: { 
                        name: 'currency', 
                        value: isInternational ? 'USD' : 'INR' 
                       } 
                    });
                    setPaymentLinkStep('hostel_fee');
                  }}
                  onCancel={() => setPaymentLinkStep('gateway')}
                  paymentFormData={paymentFormData}
                  setPaymentFormData={handleBulkUpdate}
                />
              )}

              {paymentLinkStep === 'hostel_fee' && (
                <HostelFeeMatrix
                  isInternational={isInternational}
                  academicAmount={parseFloat(feeBreakup.academic?.amount || 0)}
                  feeBreakup={feeBreakup}
                  setFeeBreakup={setFeeBreakup}
                  onNext={(hostelAmount, hostelDetails) => {
                    const academicAmount = parseFloat(feeBreakup.academic?.amount || 0);
                    const newTotal = academicAmount + hostelAmount;
                    handleInputChange({ target: { name: 'amount', value: newTotal.toString() } });
                    
                    // Pre-fill required fields for Fee Payment since they'll be hidden
                    if (isFeePayment) {
                      if (!selectedApplicationForm && applicationForms.length > 0) {
                        setSelectedApplicationForm(applicationForms[0]);
                      }
                      handleInputChange({ target: { name: 'description', value: 'Course and Hostel Fee Payment' } });
                      
                      // Also ensure a catalog is selected if not already
                      if (!selectedCatalog && paymentCatalogs.length > 0) {
                        handleSelectCatalog({ value: paymentCatalogs[0] });
                      }
                    }
                    
                    setPaymentLinkStep('details');
                  }}
                  onBack={() => setPaymentLinkStep('academic_fee')}
                />
              )}

              {paymentLinkStep === 'details' && (
                <PaymentDetailsForm
                  selectedGateway={selectedGateway}
                  paymentCatalogs={paymentCatalogs}
                  isLoadingCatalogs={isLoadingCatalogs}
                  selectedCatalog={selectedCatalog}
                  applicationForms={applicationForms}
                  isLoadingApplicationForms={isLoadingApplicationForms}
                  selectedApplicationForm={selectedApplicationForm}
                  paymentFormData={paymentFormData}
                  isCreatingLink={isCreatingLink}
                  onSelectCatalog={handleSelectCatalog}
                  onInputChange={handleInputChange}
                  setSelectedApplicationForm={setSelectedApplicationForm}
                  showActions={false}
                  isFeePayment={isFeePayment}
                  isInternational={isInternational}
                  feeBreakup={feeBreakup}
                  leadData={leadData}
                />
              )}

              {paymentLinkStep === 'success' && (
                <PaymentLinkSuccess
                  createdLinkData={createdLinkData}
                  copySuccess={copySuccess}
                  onCopy={handleCopyPaymentLink}
                  selectedGateway={selectedGateway}
                  showFooterButton={false}
                  isFeePayment={isFeePayment}
                  leadData={leadData}
                  feeBreakup={feeBreakup}
                  onClose={handleClose}
                />
              )}
            </div>

            {/* Footer (fixed) */}
            <div className="dark:bg-dark-700 flex justify-end gap-3 border-t border-gray-200 bg-white px-4 py-3 sm:px-5 dark:border-gray-700">
              {paymentLinkStep === 'details' && (
                <>
                  <Button
                    variant="flat"
                    onClick={() => setPaymentLinkStep(isFeePayment ? 'hostel_fee' : 'gateway')}
                    disabled={isCreatingLink}
                    className="min-w-[7rem] font-bold text-[#fe0046] hover:bg-red-50 hover:text-[#d0003a] transition-all"
                    unstyled={true}
                  >
                    <span>Back</span>
                    <ArrowRightIcon className="ml-1.5 h-4 w-4 stroke-[2.5px]" />
                  </Button>
                  <Button
                    color="primary"
                    onClick={handleCreatePaymentLink}
                    disabled={isCreatingLink || !selectedCatalog || !paymentFormData.amount || 
                      (selectedGateway?.GatewayCode === 'MAN' && (!paymentFormData.paymentMethod || (paymentFormData.paymentMethod !== 'cash' && !paymentFormData.transactionId)))}
                    className="min-w-[7rem] rounded-full"
                  >
                    {isCreatingLink ? (selectedGateway?.GatewayCode === 'MAN' ? 'Saving...' : 'Creating...') : (selectedGateway?.GatewayCode === 'MAN' ? 'Save Payment' : 'Create Link')}
                  </Button>
                </>
              )}

              {paymentLinkStep === 'success' && (
                <Button 
                  variant="solid" 
                  color="primary" 
                  onClick={handleClose} 
                  className="min-w-[7rem] rounded-full"
                >
                  Done
                </Button>
              )}
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};
